/**
 * ptキャンペーンの帯の表示の計算
 * 掲示とサーバー時刻から、出すかどうか・並び・文言を決めます。
 */

import type { PointCampaignBoardItem } from '../../../shared/services/pointCampaignBoardService';

/** 1分（ms） */
const MINUTE_MS = 60 * 1000;
/** 1時間（分） */
const HOUR_MINUTES = 60;
/** 1日（分） */
const DAY_MINUTES = 24 * HOUR_MINUTES;

/** 掲示の段階。hidden: まだ出さない / announcing: 予告 / active: 開催中 / ended: 終わった */
export type PointCampaignPhase = 'hidden' | 'announcing' | 'active' | 'ended';

/** 帯に出す1件 */
export type PointCampaignBandEntry = {
  item: PointCampaignBoardItem;
  phase: 'announcing' | 'active';
};

/**
 * 掲示の段階を決める
 * @param {PointCampaignBoardItem} item - 掲示
 * @param {number} nowMs - サーバー時刻
 * @returns {PointCampaignPhase} 段階
 */
export const resolvePointCampaignPhase = (item: PointCampaignBoardItem, nowMs: number): PointCampaignPhase => {
  if (nowMs >= item.endsAtMs) return 'ended';
  if (nowMs >= item.startsAtMs) return 'active';
  return nowMs >= (item.noticeFromMs ?? item.startsAtMs) ? 'announcing' : 'hidden';
};

/**
 * 帯に出す掲示を選んで並べる。開催中を終わりの近い順に、その後ろへ予告を開始の近い順に並べる
 * @param {ReadonlyArray<PointCampaignBoardItem>} items - 掲示
 * @param {number} nowMs - サーバー時刻
 * @returns {PointCampaignBandEntry[]} 帯に出す掲示
 */
export const selectPointCampaignBandEntries = (
  items: ReadonlyArray<PointCampaignBoardItem>,
  nowMs: number,
): PointCampaignBandEntry[] => {
  /** 掲示と段階の組 */
  const withPhase = items.map((item) => ({ item, phase: resolvePointCampaignPhase(item, nowMs) }));
  return [
    ...withPhase
      .filter((entry) => entry.phase === 'active')
      .sort((a, b) => a.item.endsAtMs - b.item.endsAtMs),
    ...withPhase
      .filter((entry) => entry.phase === 'announcing')
      .sort((a, b) => a.item.startsAtMs - b.item.startsAtMs),
  ] as PointCampaignBandEntry[];
};

/**
 * 1分以上の長さを文言にする（切り上げ）
 * @param {number} durationMs - 長さ
 * @returns {string} 文言
 */
const formatDuration = (durationMs: number): string => {
  /** 切り上げた分 */
  const totalMinutes = Math.ceil(durationMs / MINUTE_MS);
  if (totalMinutes >= DAY_MINUTES) return `${Math.ceil(totalMinutes / DAY_MINUTES)}日`;
  if (totalMinutes < HOUR_MINUTES) return `${totalMinutes}分`;
  /** 時間の端数の分 */
  const minutes = totalMinutes % HOUR_MINUTES;
  return `${Math.floor(totalMinutes / HOUR_MINUTES)}時間${minutes > 0 ? `${minutes}分` : ''}`;
};

/**
 * 終了までの残り時間の文言を作る
 * @param {number} remainingMs - 終了までの時間
 * @returns {string} 文言
 */
export const formatPointCampaignRemaining = (remainingMs: number): string => (
  remainingMs < MINUTE_MS ? '残り1分未満' : `残り${formatDuration(remainingMs)}`
);

/**
 * 開始までの時間の文言を作る
 * @param {number} untilStartMs - 開始までの時間
 * @returns {string} 文言
 */
export const formatPointCampaignStartsIn = (untilStartMs: number): string => (
  untilStartMs < MINUTE_MS ? 'まもなく開始' : `あと${formatDuration(untilStartMs)}で開始`
);
